import { t } from 'i18next';
import m from 'mithril';
import React, { type FC, useState } from 'react';

import MActivityCreationModal from 'Components/modal/MActivityCreationModal/MActivityCreationModal';
import Enum from 'Models/Enum';
import {
  getBreakingNewsDefinition,
  getDailySerieDefinition,
} from 'Pages/Activity/ActivityList/components/ActivitiesDescription';

import { useBreakingNewsForm } from './useBreakingNewsForm';
import { useCampaignForm } from './useCampaignForm';

export type CreateActivityModalProps = {
  visible: boolean;
  onClose: () => void;
};

const STEPS = {
  CHOOSING_ACTIVITY: 0,
  CREATE_CAMPAIGN: 1,
  CREATE_BREAKING_NEWS: 2,
};

const STEP_MAPPING: Record<string, keyof typeof STEPS> = {
  [Enum.activityTypeEnum.DAILY_SERIE]: 'CREATE_CAMPAIGN',
  [Enum.activityTypeEnum.BREAKING_NEWS]: 'CREATE_BREAKING_NEWS',
};

const REDIRECTION_MAPPING = {
  [Enum.activityTypeEnum.BREAKING_NEWS]: '/activity/breakingnews/new',
  [Enum.activityTypeEnum.WELCOME]: '/activity/welcome/new',
};

export const CreateActivityModal: FC<CreateActivityModalProps> = ({ visible, onClose }) => {
  const [step, setStep] = useState<number>(STEPS.CHOOSING_ACTIVITY);
  const handleGoBack = () => setStep(STEPS.CHOOSING_ACTIVITY);

  const {
    form: campaignForm,
    reset: resetCampaignForm,
    submit: submitCampaignForm,
    canSubmit: canSubmitCampaignForm,
  } = useCampaignForm({ onGoBack: handleGoBack });

  const {
    form: breakingNewsForm,
    reset: resetBreakingNewsForm,
    submit: submitBreakingNewsForm,
    canSubmit: canSubmitBreakingNewsForm,
  } = useBreakingNewsForm({ onGoBack: handleGoBack });

  const disableActionButton =
    (step === STEPS.CREATE_CAMPAIGN && !canSubmitCampaignForm) ||
    (step === STEPS.CREATE_BREAKING_NEWS && !canSubmitBreakingNewsForm);

  const titleMapping = {
    [STEPS.CHOOSING_ACTIVITY]: t('activities:modals.activity_creation_modal.activity_title'),
    [STEPS.CREATE_CAMPAIGN]: t('activities:modals.activity_creation_modal.campaign_title'),
    [STEPS.CREATE_BREAKING_NEWS]: t('activities:modals.activity_creation_modal.activity_title'),
  };

  const actionButtonMapping = {
    [STEPS.CHOOSING_ACTIVITY]: t('activities:modals.activity_creation_modal.activity_title'),
    [STEPS.CREATE_CAMPAIGN]: t('activities:modals.activity_creation_modal.campaign_title'),
    [STEPS.CREATE_BREAKING_NEWS]: t('activities:modals.activity_creation_modal.breaking_new_action'),
  };

  /**
   * Due to the way modals are made, the handler for the action button is decoupled from the modal's content.
   * This means that we need to handle both step advancement and action dispatching here.
   */
  const onAction = (type: number) => {
    if (step === STEPS.CHOOSING_ACTIVITY) {
      return setStep(STEPS[STEP_MAPPING[type]]);
    }

    if (step === STEPS.CREATE_CAMPAIGN) {
      submitCampaignForm();
      return;
    }

    if (step === STEPS.CREATE_BREAKING_NEWS) {
      submitBreakingNewsForm();
      return;
    }

    // All non-handled activity type are redirected to their corresponding form pages
    m.route(REDIRECTION_MAPPING[type]);
  };

  const handleClose = () => {
    onClose();
    setStep(STEPS.CHOOSING_ACTIVITY);
    resetCampaignForm();
    resetBreakingNewsForm();
  };

  return (
    <MActivityCreationModal
      visible={visible}
      isLoading={false}
      onClose={handleClose}
      activities={[getDailySerieDefinition(), getBreakingNewsDefinition()]}
      labelTab={t('activities:modals.activity_creation_modal.tab_label')}
      labelSecondButton={t('activities:modals.activity_creation_modal.cancel')}
      labelNextAction={t('activities:modals.activity_creation_modal.next')}
      title={titleMapping[step]}
      labelActionButton={actionButtonMapping[step]}
      onCreate={onAction}
      onSecondAction={handleClose}
      isFirstStep={step === STEPS.CHOOSING_ACTIVITY}
      disableActionButton={disableActionButton}
    >
      {step === STEPS.CREATE_CAMPAIGN ? campaignForm : null}
      {step === STEPS.CREATE_BREAKING_NEWS ? breakingNewsForm : null}
    </MActivityCreationModal>
  );
};
